import React from 'react';
import { Link } from 'react-router-dom';
import AssessmentPage from './AssessmentPage';

const AdhdAssessment = () => (
  <AssessmentPage
    seo={{
      title: 'ADHD Assessment | Clinical Minds Psychology | Coffs Harbour & Australia-wide',
      description: 'Thorough ADHD assessments for young people and adults, with practical recommendations for home, school and work. Done personally, start to finish, by one psychologist.',
      canonicalUrl: 'https://www.clinicalminds.net/adhd-assessment'
    }}
    heroTitle="ADHD Assessment"
    heroSubtitle=""
    intro={
      <p className="body-medium">
        ADHD is one of the most talked-about diagnoses there is, and one of the most misunderstood. A proper assessment cuts through the noise. It looks carefully at attention, impulsivity and executive functioning, but also at everything else that can look like ADHD or hide alongside it, so you get a careful, well-founded answer. My approach is neuroaffirming: many people with ADHD describe attention that follows interest, and the goal of assessment is to understand how your attention works, not to catalogue failures.
      </p>
    }
    whoFor={
      <p className="body-medium">
        Children whose teachers or parents are seeing attention, organisation or impulse-control struggles that effort alone isn't fixing. Teenagers whose results don't match their ability. Adults who have spent years working twice as hard to hold things together and want to know why. And anyone who needs a formal diagnostic report to access support, adjustments or a medication review with their doctor.
      </p>
    }
    whatsInvolved={
      <p className="body-medium">
        A thorough ADHD assessment is much more than a checklist. It typically includes a detailed clinical interview covering history from childhood onward, standardised rating scales completed by people who see you in different settings, and cognitive assessment where it adds value, because understanding how you think helps separate ADHD from other explanations such as learning difficulties, anxiety or sleep problems. We prioritise time with the person being assessed: an intake session plus at least two assessment sessions, because good assessment can't be done in a single visit. You then receive a plain-language feedback session and a full written report.
      </p>
    }
    reportUsedFor={
      <p className="body-medium">
        The report gives a clear diagnostic outcome and recommendations that are specific to your life: school and classroom adjustments, workplace strategies, and home routines that work with an ADHD brain instead of against it. It's written to support school learning adjustments and NCCD evidence, and to inform your GP, paediatrician or psychiatrist if medication is being considered. As a psychologist I don't prescribe medication, but my report gives prescribers the diagnostic clarity they need.
      </p>
    }
    feesContent={
      <>
        <p className="body-medium">
          Assessment fees depend on the scope of the assessment. See our <Link to="/fees" className="assess-link">fees page</Link> for information about funding options including the NDIS, or <Link to="/contact" className="assess-link">contact us</Link> and we'll talk it through.
        </p>
      </>
    }
    ctaText="Enquire about an ADHD assessment"
    enquiryParam="adhd"
    extraSections={
      <>
        <section className="assess-section scroll-reveal" data-testid="assess-how-works">
          <div className="container assess-narrow">
            <h2 className="heading-1">How my ADHD assessments work</h2>
            <div className="assess-body">
              <p className="body-medium">
                Same structure as all my assessments: an hour with you or your family first, at least two assessment sessions on different days, then plain-language feedback, practical recommendations, and a personal profile written for the person assessed. For ADHD specifically, the assessment includes cognitive testing, structured rating scales completed by the people who know you best, and a careful look at how attention and executive functioning show up across different settings.
              </p>
            </div>
          </div>
        </section>
        <section className="assess-section assess-alt scroll-reveal" data-testid="assess-common-questions">
          <div className="container assess-narrow">
            <h2 className="heading-1">Common questions</h2>
            <div className="assess-body assess-qa">
              <p className="body-medium"><strong>Do you assess adults for ADHD?</strong> Yes, adults are a regular part of my assessment work, including people exploring this for the first time in their 30s, 40s and beyond.</p>
              <p className="body-medium"><strong>Can you prescribe medication?</strong> No, as a psychologist I don't prescribe. If medication is something you want to explore, my report is written to give your GP or a psychiatrist exactly what they need for that conversation.</p>
              <p className="body-medium"><strong>What's the difference between this and a quick online screener?</strong> Screeners ask whether ADHD is possible. An assessment works out what's actually going on, including the things that can look like ADHD but aren't, and what will genuinely help either way.</p>
            </div>
          </div>
        </section>
      </>
    }
    faqs={[
      {
        question: 'Do we need a referral?',
        answer: "You can enquire directly without a referral. A referral from a GP or paediatrician can help with rebates in some situations, and we'll let you know if that applies to you."
      },
      {
        question: 'Can ADHD and autism be assessed together?',
        answer: "They can be. ADHD and autism often co-occur and can look alike on the surface. Where both are in question, we'll talk through whether a combined assessment or a staged approach makes sense for your situation, and we'll only recommend what the question actually needs."
      },
      {
        question: 'My child did fine in the assessment room. Does that rule out ADHD?',
        answer: "No. A quiet, novel, one-on-one setting is exactly where ADHD is least visible. That's why we gather information from home and school, where demands on attention are real and sustained."
      },
      {
        question: 'Do you assess adults for ADHD?',
        answer: "Yes. Adult assessment includes a careful developmental history, because ADHD begins in childhood even when it isn't recognised until much later."
      }
    ]}
  />
);

export default AdhdAssessment;
